/**
 * Runtime environment detection. Routes plugin selection between
 * web / installed-PWA / Capacitor native shells. The same web bundle
 * runs in all three.
 *
 * Always SSR-safe: `typeof window` is the first check on every accessor.
 */

export type RuntimePlatform =
  | 'web'
  | 'pwa-standalone'
  | 'capacitor-ios'
  | 'capacitor-android'

declare global {
  interface Window {
    Capacitor?: {
      isNativePlatform?: () => boolean
      getPlatform?: () => string
    }
  }
  interface Navigator {
    standalone?: boolean
  }
}

function inBrowser(): boolean {
  return typeof window !== 'undefined'
}

export function isNative(): boolean {
  return inBrowser() && window.Capacitor?.isNativePlatform?.() === true
}

export function isCapacitorIOS(): boolean {
  return isNative() && window.Capacitor?.getPlatform?.() === 'ios'
}

export function isCapacitorAndroid(): boolean {
  return isNative() && window.Capacitor?.getPlatform?.() === 'android'
}

export function isStandalonePWA(): boolean {
  if (!inBrowser() || isNative()) return false
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  if (navigator.standalone === true) return true
  return false
}

/**
 * iPhone, iPod or iPad. iPadOS 13+ reports a desktop Safari user agent, so only
 * its touch-point count separates it from a Mac — the same test
 * `public/qr.html` makes, which cannot import this.
 */
export function isIOSDevice(): boolean {
  if (!inBrowser()) return false
  const ua = navigator.userAgent
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  )
}

/**
 * Phone or tablet form factor — coarse pointer (touch primary) is the
 * cleanest signal that "phone OR tablet". On a typical laptop this is
 * false; on iPhone, iPad, Android phones and tablets it's true. Used to open
 * the camera straight away when adding a photo — a wrong call only means one
 * extra tap.
 */
export function isMobileOrTablet(): boolean {
  if (!inBrowser()) return false
  if (window.matchMedia?.('(pointer: coarse)').matches) return true
  return false
}

export function getPlatform(): RuntimePlatform {
  if (isCapacitorIOS()) return 'capacitor-ios'
  if (isCapacitorAndroid()) return 'capacitor-android'
  if (isStandalonePWA()) return 'pwa-standalone'
  return 'web'
}
